import { useEffect, useRef, RefObject } from 'react';

/**
 * Calls `handler` when a mousedown occurs outside the element referenced by `ref`.
 *
 * @param ref     - Ref to the element that should be considered "inside"
 * @param handler - Callback invoked on an outside mousedown
 * @param enabled - Whether the listener is active (default: true)
 */
export function useClickAway<T extends HTMLElement>(
    ref: RefObject<T | null>,
    handler: (event: MouseEvent) => void,
    enabled: boolean = true
) {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!enabled) return;

        const handleMouseDown = (e: MouseEvent) => {
            // Ignore clicks inside MUI Autocomplete/Popper portals rendered outside the DOM tree
            if ((e.target as Element)?.closest?.('.MuiAutocomplete-popper, .MuiPopper-root')) return;

            const inside = ref.current?.contains(e.target as Node) ?? false;
            if (!inside) {
                handlerRef.current(e);
            }
        };

        document.addEventListener('mousedown', handleMouseDown, true);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown, true);
        };
    }, [ref, enabled]);
}
